import {
  createMiniSwarmWaitingLoopFrames,
  createSplitSwarmDistributeFrames,
  MINI_DISTRIBUTION_LOOP_DURATION,
} from "../../core/split-swarm-keyframes.js";

export const DISTRIBUTION_COMPLETE_SIGNAL = "course-distribution-complete";

/**
 * Loading-Loop-Vertrag für das Portal:
 * 1. `controller.play("split-swarm-distribute")` startet Split und Verteilung genau einmal.
 * 2. Danach werden ausschließlich endliche Hüpfzyklen der sieben Mini-Orbis wiederholt.
 *    Ihre Keyframes werden direkt und unverändert aus dem ursprünglichen autonomen
 *    Schwarmabschnitt extrahiert. Nur ein physischer Rücksprung zum Startpunkt wird ergänzt,
 *    damit sich dieser ansonsten identische Abschnitt ohne sichtbares Teleportieren loopen lässt.
 * 3. Sobald der Ladebalken fertig ist, sendet das Portal
 *    `controller.notify("course-distribution-complete")`.
 * 4. Der aktuelle Hüpfzyklus landet sauber, der Schwarm bleibt sichtbar und eine spätere
 *    Bestätigungs-Haken-Sequenz kann anschließend separat gestartet werden.
 *
 * Kein `loop: Infinity` verwenden: Der Signal-Loop gehört dieser Sequenz und wiederholt
 * absichtlich weder das Aufblasen noch das Zerplatzen des großen Orbis.
 */

export const meta = Object.freeze({
  id: "split-swarm-distribute",
  label: "Mini-Orbi-Schwarm-Verteilen",
  duration: 6800,
  persistFinal: true,
  managesOwnLoop: true,
  description: "In sieben eigenständige Mini-Orbis zerplatzen, über die Stage verteilen und ohne Merge sichtbar stehen bleiben.",
  tags: ["body", "split", "swarm", "bounce", "distribute", "courses", "confirmation", "one-shot"],
});

export async function run({ elements, scene, animate, signal, waitForSignal }) {
  if (!scene?.miniSwarm || scene.miniBodies.length !== 7 || scene.miniShadows.length !== 7) {
    throw new Error("Die Mini-Orbi-Verteilsequenz benötigt den vollständigen Schwarm in der Stage.");
  }

  let completionRequested = false;
  const completionPromise = typeof waitForSignal === "function"
    ? waitForSignal(DISTRIBUTION_COMPLETE_SIGNAL).then((result) => {
      if (!result.aborted) completionRequested = true;
      return result;
    })
    : Promise.resolve({ name: DISTRIBUTION_COMPLETE_SIGNAL, aborted: false }).then((result) => {
      completionRequested = true;
      return result;
    });

  const frames = createSplitSwarmDistributeFrames();
  const options = { duration: meta.duration, easing: "linear" };
  const animations = [
    animate(elements.actor, frames.mainActor, options),
    animate(elements.shadow, frames.mainShadow, options),
    animate(elements.svg, frames.mainBlob, options),
    animate(scene.swarmRing, frames.burstRing, options),
    ...scene.miniBodies.map((body, index) => animate(body, frames.minis[index].body, options)),
    ...scene.miniShadows.map((shadow, index) => animate(shadow, frames.minis[index].shadow, options)),
    ...scene.swarmSparks.map((spark, index) => animate(spark, frames.sparks[index], options)),
  ];

  await Promise.allSettled(animations.map(({ finished }) => finished));

  let cycleIndex = 0;
  while (!completionRequested && !signal?.aborted) {
    const loopFrames = createMiniSwarmWaitingLoopFrames(cycleIndex);
    const loopOptions = {
      duration: MINI_DISTRIBUTION_LOOP_DURATION,
      easing: "linear",
      transient: true,
    };
    const loopAnimations = [
      ...scene.miniBodies.map((body, index) => animate(body, loopFrames[index].body, loopOptions)),
      ...scene.miniShadows.map((shadow, index) => animate(shadow, loopFrames[index].shadow, loopOptions)),
    ];
    await Promise.allSettled(loopAnimations.map(({ finished }) => finished));
    cycleIndex += 1;
  }

  await completionPromise;
}
